import {
  ChevronDown,
  ChevronRight,
  Minus,
  Pencil,
  Plus,
  RotateCcw,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@bigpp/ui/components/badge";
import { Button } from "@bigpp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@bigpp/ui/components/dialog";

import { executeOps } from "../bridge/office-ops.ts";
import { parseDeck, type ParsedSlide } from "../pptx.ts";
import {
  computeDiff,
  segmentRows,
  slideLabel,
  type DiffRow,
  type SlideStatus,
} from "../slideDiff.ts";
import {
  bytesToBase64,
  captureDeckBytes,
  loadSnapshot,
  restoreSingleSlide,
  restoreSnapshot,
} from "../snapshots.ts";
import { ConfirmDialog } from "./ConfirmDialog.tsx";

type Props = {
  open: boolean;
  messageId: string;
  onClose: () => void;
};

type LoadedState = {
  capturedAt: number;
  snapshotBytes: Uint8Array;
  snapshotSlides: ParsedSlide[];
  currentSlides: ParsedSlide[];
  rows: DiffRow[];
};

// Persist the IDs of any preview slides we insert into the deck so we can
// clean them up even if the tab is closed mid-preview. Cheap and reliable —
// no title-shape mutation, so PowerPoint can never reject the tag write.
const PENDING_IDS_KEY = "bigpp.compare.pendingSlideIds";

const readPendingIds = (): string[] => {
  try {
    const raw = localStorage.getItem(PENDING_IDS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === "string");
  } catch {
    return [];
  }
};

const writePendingIds = (ids: string[]): void => {
  try {
    if (ids.length === 0) localStorage.removeItem(PENDING_IDS_KEY);
    else localStorage.setItem(PENDING_IDS_KEY, JSON.stringify(ids));
  } catch {
    // ignore — worst case a sweep leaves orphans
  }
};

const sweepPendingSlides = async (): Promise<void> => {
  const ids = readPendingIds();
  if (ids.length === 0) return;
  try {
    await executeOps([{ op: "deleteSlidesByIds", ids }]);
  } catch {
    // best-effort
  } finally {
    writePendingIds([]);
  }
};

const readSlideIds = (v: unknown): string[] => {
  if (typeof v !== "object" || v === null) return [];
  for (const [k, val] of Object.entries(v)) {
    if (k === "slideIds" && Array.isArray(val)) {
      return val.filter((x): x is string => typeof x === "string");
    }
  }
  return [];
};

const formatMB = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
};

const formatTimestamp = (ts: number): string => {
  const d = new Date(ts);
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const readCount = (v: unknown): number => {
  if (typeof v !== "object" || v === null) return 0;
  for (const [k, val] of Object.entries(v)) {
    if (k === "count" && typeof val === "number") return val;
  }
  return 0;
};

const readInsertResult = (v: unknown): { firstIndex: number; count: number } => {
  if (typeof v !== "object" || v === null) {
    throw new Error("insertSlidesFromBase64 returned no data");
  }
  let firstIndex: number | null = null;
  let count: number | null = null;
  for (const [k, val] of Object.entries(v)) {
    if (k === "firstIndex" && typeof val === "number") firstIndex = val;
    if (k === "count" && typeof val === "number") count = val;
  }
  if (firstIndex === null || count === null) {
    throw new Error("insertSlidesFromBase64 result missing fields");
  }
  return { firstIndex, count };
};

const readBase64 = (v: unknown): string => {
  if (typeof v !== "object" || v === null) throw new Error("no image data");
  for (const [k, val] of Object.entries(v)) {
    if (k === "base64" && typeof val === "string") return val;
  }
  throw new Error("no base64 field on image result");
};

const statusBadge = (status: SlideStatus): { label: string; variant: "primary" | "success" | "destructive" | "default"; icon: typeof Plus } => {
  if (status === "added") return { label: "Added", variant: "success", icon: Plus };
  if (status === "removed") return { label: "Removed", variant: "destructive", icon: Minus };
  if (status === "changed") return { label: "Changed", variant: "primary", icon: Pencil };
  return { label: "Unchanged", variant: "default", icon: Pencil };
};

export function ComparePanel({ open, messageId, onClose }: Props) {
  const [state, setState] = useState<LoadedState | null>(null);
  const [loading, setLoading] = useState(false);
  const [captureProgress, setCaptureProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [currentThumbs, setCurrentThumbs] = useState<Map<number, string>>(new Map());
  const [snapshotThumbs, setSnapshotThumbs] = useState<Map<number, string>>(new Map());
  const [thumbsLoading, setThumbsLoading] = useState(false);
  const [thumbsFetched, setThumbsFetched] = useState(false);
  const [revertDeckOpen, setRevertDeckOpen] = useState(false);
  const [revertTarget, setRevertTarget] = useState<{
    snapshotIndex: number;
    currentIndex: number;
  } | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setCaptureProgress(null);
    setError(null);
    setState(null);
    setExpanded(new Set());
    setCurrentThumbs(new Map());
    setSnapshotThumbs(new Map());
    setThumbsFetched(false);

    void (async () => {
      try {
        // Sweep any orphans from a previous session before we start.
        await sweepPendingSlides();
        const snap = await loadSnapshot(messageId);
        if (!snap) throw new Error("Snapshot is no longer available.");
        const currentBytes = await captureDeckBytes((loaded, total) => {
          if (!cancelled) setCaptureProgress({ loaded, total });
        });
        const [snapshotSlides, currentSlides] = await Promise.all([
          parseDeck(snap.bytes),
          parseDeck(currentBytes),
        ]);
        const rows = computeDiff(currentSlides, snapshotSlides);
        if (cancelled) return;
        setState({
          capturedAt: snap.capturedAt,
          snapshotBytes: snap.bytes,
          snapshotSlides,
          currentSlides,
          rows,
        });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // Belt-and-suspenders: sweep on close too in case cleanup didn't finish.
      void sweepPendingSlides();
    };
  }, [open, messageId]);

  const ensureThumbnails = async (loaded: LoadedState): Promise<void> => {
    if (thumbsFetched || thumbsLoading) return;
    setThumbsLoading(true);
    setError(null);
    try {
      const changedRows = loaded.rows.filter((r) => r.status !== "unchanged");
      const currentIndexes = changedRows
        .map((r) => r.currentIndex)
        .filter((idx): idx is number => idx !== null);
      const snapshotIndexes = changedRows
        .map((r) => r.snapshotIndex)
        .filter((idx): idx is number => idx !== null);

      const nextCurrent = new Map<number, string>();
      for (const idx of currentIndexes) {
        const [img] = await executeOps([
          { op: "getSlideImage", slide: idx, width: 400 },
        ]);
        nextCurrent.set(idx, readBase64(img));
      }
      setCurrentThumbs(nextCurrent);

      if (snapshotIndexes.length > 0) {
        const base64 = bytesToBase64(loaded.snapshotBytes);
        const targetIndexes = snapshotIndexes.filter(
          (idx) => idx < loaded.snapshotSlides.length,
        );
        let insertedIds: string[] = [];
        try {
          const [countRes, insertRes] = await executeOps([
            { op: "countSlides" },
            { op: "insertSlidesFromBase64", base64 },
          ]);
          const originalCount = readCount(countRes);
          const { firstIndex } = readInsertResult(insertRes);
          insertedIds = readSlideIds(insertRes);
          // Persist IDs immediately so a crash/close between here and the
          // finally block still lets the next session clean them up.
          if (insertedIds.length > 0) writePendingIds(insertedIds);
          void firstIndex;
          const imageResults = await executeOps(
            targetIndexes.map(
              (idx) =>
                ({
                  op: "getSlideImage" as const,
                  slide: originalCount + idx,
                  width: 400,
                }),
            ),
          );
          const nextSnapshot = new Map<number, string>();
          for (let i = 0; i < targetIndexes.length; i++) {
            const imgResult = imageResults[i];
            const idx = targetIndexes[i];
            if (idx === undefined || imgResult === undefined) continue;
            nextSnapshot.set(idx, readBase64(imgResult));
          }
          setSnapshotThumbs(nextSnapshot);
        } finally {
          if (insertedIds.length > 0) {
            try {
              await executeOps([{ op: "deleteSlidesByIds", ids: insertedIds }]);
            } catch {
              // best-effort; sweep on next open will retry
            } finally {
              writePendingIds([]);
            }
          }
        }
      }
      setThumbsFetched(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setThumbsLoading(false);
    }
  };

  const toggleRow = (row: DiffRow): void => {
    if (!state) return;
    const next = new Set(expanded);
    if (next.has(row.key)) next.delete(row.key);
    else next.add(row.key);
    setExpanded(next);
    if (!thumbsFetched && !thumbsLoading) {
      void ensureThumbnails(state);
    }
  };

  const handleRevertDeck = async (): Promise<void> => {
    if (!state) return;
    setPending(true);
    try {
      await restoreSnapshot(state.snapshotBytes);
      toast.success(
        `Deck restored to version from ${formatTimestamp(state.capturedAt)}`,
      );
      setRevertDeckOpen(false);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(false);
    }
  };

  const handleRevertSlide = async (): Promise<void> => {
    if (!state || revertTarget === null) return;
    const slideNumber = revertTarget.currentIndex + 1;
    setPending(true);
    try {
      await restoreSingleSlide(
        state.snapshotBytes,
        revertTarget.snapshotIndex,
        revertTarget.currentIndex,
      );
      toast.success(
        `Slide ${slideNumber.toString()} restored to version from ${formatTimestamp(state.capturedAt)}`,
      );
      setRevertTarget(null);
      const currentBytes = await captureDeckBytes();
      const currentSlides = await parseDeck(currentBytes);
      const rows = computeDiff(currentSlides, state.snapshotSlides);
      setState({ ...state, currentSlides, rows });
      setCurrentThumbs(new Map());
      setSnapshotThumbs(new Map());
      setThumbsFetched(false);
      setExpanded(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(false);
    }
  };

  const summary = state
    ? {
        snapshot: state.snapshotSlides.length,
        current: state.currentSlides.length,
        changed: state.rows.filter((r) => r.status === "changed").length,
        added: state.rows.filter((r) => r.status === "added").length,
        removed: state.rows.filter((r) => r.status === "removed").length,
      }
    : null;

  const hasDiffs = state ? state.rows.some((r) => r.status !== "unchanged") : false;
  const segments = state ? segmentRows(state.rows) : [];

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!pending && !v) onClose();
      }}
    >
      <DialogContent
        className="w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] p-0 gap-0 flex flex-col overflow-hidden"
      >
        <DialogHeader className="border-b border-[var(--color-border)] px-3 py-2">
          <DialogTitle className="text-sm font-semibold">
            Compare with earlier version
          </DialogTitle>
          {state && (
            <div className="text-[11px] text-[var(--color-muted-foreground)]">
              {formatTimestamp(state.capturedAt)}
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-col gap-3">
        {loading && (
          <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
            <span className="size-2 rounded-full bg-[var(--color-muted-foreground)] animate-pulse" />
            {captureProgress && captureProgress.total > 0
              ? `Reading deck… ${formatMB(captureProgress.loaded)} / ${formatMB(captureProgress.total)}`
              : "Analyzing snapshot…"}
          </div>
        )}

        {error && !loading && (
          <div className="text-xs text-[var(--color-destructive)]">{error}</div>
        )}

        {state && summary && (
          <div className="text-[11px] text-[var(--color-muted-foreground)]">
            Snapshot: {summary.snapshot.toString()} slides · Current:{" "}
            {summary.current.toString()} · Changed: {summary.changed.toString()}{" "}
            · Added: {summary.added.toString()} · Removed:{" "}
            {summary.removed.toString()}
          </div>
        )}

        {state && hasDiffs && (
          <div className="rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-secondary)] px-2.5 py-1.5 text-[11px] text-[var(--color-muted-foreground)]">
            To render snapshot previews, PowerSlide temporarily inserts the
            versioned slides into your deck and removes them as soon as the
            thumbnails are captured.
          </div>
        )}

        {state && !hasDiffs && (
          <div className="rounded-md border border-[var(--color-border)] p-4 text-center text-xs text-[var(--color-muted-foreground)]">
            Deck matches snapshot exactly.
          </div>
        )}

        {state && hasDiffs && (
          <div className="flex flex-col gap-1.5">
            {segments.map((seg, segIdx) => {
              if (seg.kind === "unchanged") {
                return (
                  <div
                    key={`u-${segIdx.toString()}`}
                    className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)] px-2 py-1"
                  >
                    {seg.count.toString()} unchanged{" "}
                    {seg.count === 1 ? "slide" : "slides"}
                  </div>
                );
              }
              return (
                <div key={`c-${segIdx.toString()}`} className="flex flex-col gap-1.5">
                  {seg.rows.map((row) => (
                    <SlideRow
                      key={row.key}
                      row={row}
                      expanded={expanded.has(row.key)}
                      onToggle={() => {
                        toggleRow(row);
                      }}
                      currentThumb={
                        row.currentIndex !== null
                          ? (currentThumbs.get(row.currentIndex) ?? null)
                          : null
                      }
                      snapshotThumb={
                        row.snapshotIndex !== null
                          ? (snapshotThumbs.get(row.snapshotIndex) ?? null)
                          : null
                      }
                      thumbsLoading={thumbsLoading}
                      onRestore={() => {
                        if (
                          row.snapshotIndex === null ||
                          row.currentIndex === null
                        ) {
                          return;
                        }
                        setRevertTarget({
                          snapshotIndex: row.snapshotIndex,
                          currentIndex: row.currentIndex,
                        });
                      }}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

        {state && (
          <div className="border-t border-[var(--color-border)] px-3 py-2 flex justify-end">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setRevertDeckOpen(true);
              }}
            >
              <RotateCcw className="size-3.5" />
              Revert entire deck
            </Button>
          </div>
        )}

        <ConfirmDialog
          open={revertDeckOpen}
          onOpenChange={(v) => {
            if (!pending) setRevertDeckOpen(v);
          }}
          title="Revert entire deck?"
          description="Replace the live deck contents with this snapshot. Any edits since then will be lost."
          confirmLabel="Revert deck"
          destructive
          pending={pending}
          onConfirm={() => {
            void handleRevertDeck();
          }}
        />

        <ConfirmDialog
          open={revertTarget !== null}
          onOpenChange={(v) => {
            if (!pending && !v) setRevertTarget(null);
          }}
          title="Restore this slide?"
          description={
            revertTarget !== null
              ? `Replace slide ${(revertTarget.currentIndex + 1).toString()} with the snapshot version.`
              : undefined
          }
          confirmLabel="Restore slide"
          destructive
          pending={pending}
          onConfirm={() => {
            void handleRevertSlide();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function SlideRow({
  row,
  expanded,
  onToggle,
  currentThumb,
  snapshotThumb,
  thumbsLoading,
  onRestore,
}: {
  row: DiffRow;
  expanded: boolean;
  onToggle: () => void;
  currentThumb: string | null;
  snapshotThumb: string | null;
  thumbsLoading: boolean;
  onRestore: () => void;
}) {
  const badge = statusBadge(row.status);
  const BadgeIcon = badge.icon;
  const canRestore =
    row.status === "changed" &&
    row.snapshotIndex !== null &&
    row.currentIndex !== null;
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-left"
      >
        {expanded ? (
          <ChevronDown className="size-3.5 text-[var(--color-muted-foreground)]" />
        ) : (
          <ChevronRight className="size-3.5 text-[var(--color-muted-foreground)]" />
        )}
        <span className="text-xs font-medium">{slideLabel(row)}</span>
        <Badge variant={badge.variant}>
          <BadgeIcon className="size-3" />
          {badge.label}
        </Badge>
      </button>
      {expanded && (
        <div className="border-t border-[var(--color-border)] p-2 flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <ThumbnailPane
              label="Current"
              base64={row.status === "removed" ? null : currentThumb}
              placeholder={row.status === "removed" ? "(deleted)" : null}
              loading={thumbsLoading}
            />
            <ThumbnailPane
              label="Snapshot"
              base64={row.status === "added" ? null : snapshotThumb}
              placeholder={row.status === "added" ? "(not in snapshot)" : null}
              loading={thumbsLoading}
            />
          </div>
          {row.status !== "unchanged" && (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="secondary"
                onClick={onRestore}
                disabled={!canRestore}
                title={
                  row.status === "added"
                    ? "This slide isn't in the snapshot"
                    : row.status === "removed"
                      ? "Removed slides can't be restored individually"
                      : undefined
                }
              >
                <RotateCcw className="size-3.5" />
                Restore this slide
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ThumbnailPane({
  label,
  base64,
  placeholder,
  loading,
}: {
  label: string;
  base64: string | null;
  placeholder: string | null;
  loading: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
        {label}
      </div>
      <div className="aspect-video rounded border border-[var(--color-border)] bg-[var(--color-secondary)] overflow-hidden flex items-center justify-center">
        {base64 ? (
          <img
            src={`data:image/png;base64,${base64}`}
            alt={label}
            className="w-full h-full object-contain"
          />
        ) : placeholder ? (
          <span className="text-[10px] text-[var(--color-muted-foreground)]">
            {placeholder}
          </span>
        ) : loading ? (
          <span className="size-2 rounded-full bg-[var(--color-muted-foreground)] animate-pulse" />
        ) : (
          <span className="text-[10px] text-[var(--color-muted-foreground)]">
            Expand to load
          </span>
        )}
      </div>
    </div>
  );
}
